import { ReactNode, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { SocialPlatform, SocialTopicAuthor } from "@digital-twins/shared-types";
import { SocialPlatformIcon } from "./SocialPlatformIcon";
import { SocialAuthorAvatar } from "./SocialPostTile";
import { initialsOf } from "./SocialTopVoices";
import { SocialTopicSlotContext } from "./SocialTopicView";
import { SocialFilterMenu, SocialFilterMenuOption } from "./SocialFilterPopover";
import { formatSocialCount } from "./socialFormat";
import { useSocialTopicAuthors } from "./useSocialTopicAuthors";

/** The authors shown at a time; a search narrows them on the server. */
export const SOCIAL_AUTHOR_MENU_LIMIT = 20;

/** The author search waits this long after the last keystroke before it asks the server. */
export const SOCIAL_AUTHOR_SEARCH_DEBOUNCE_MS = 250;

function normalizedHandle(handle: string): string {
  return handle.trim().replace(/^@+/, "").toLowerCase();
}

/** The menu's value for an author: `<platform>:<handle>`, the same shape as the URL param. */
export function socialAuthorOptionKey(author: { platform: SocialPlatform; handle: string }): string {
  return `${author.platform}:${normalizedHandle(author.handle)}`;
}

interface SocialAuthorOptionLabelProps {
  author: SocialTopicAuthor;
  index: number;
}

function SocialAuthorOptionLabel(props: SocialAuthorOptionLabelProps) {
  const { author, index } = props;

  const handle = author.handle.replace(/^@+/, "");

  return (
    <span className="inline-flex min-w-0 items-center gap-2">
      <SocialAuthorAvatar
        name={author.displayName ?? handle}
        platform={author.platform}
        avatarPath={author.authorAvatarPath}
        initials={initialsOf(author)}
        size="xs"
        testId={`topic-filter-author-avatar-img-${index}`}
      />
      <span className="truncate">@{handle}</span>
      <SocialPlatformIcon platform={author.platform} className="size-3.5 shrink-0 text-muted-foreground" />
    </span>
  );
}

interface SocialAuthorFilterPopoverProps {
  context: SocialTopicSlotContext;
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
  /** The Author chip. */
  trigger: ReactNode;
}

/**
 * The Author chip's menu: the Topic's top authors (reach then posts, as the
 * Top voices card), under the other active filters, each with the stored
 * picture, handle, platform and post count. The search asks the server
 * (handle or name), so the full author list is never loaded.
 */
export function SocialAuthorFilterPopover(props: SocialAuthorFilterPopoverProps) {
  const { context, isOpen, onOpenChange, trigger } = props;

  const { t, i18n } = useTranslation("app");
  const { topicFilters, universe, refreshKey } = context;
  const { metric, author: selectedAuthor } = topicFilters.filters;
  const [draft, setDraft] = useState("");
  const [search, setSearch] = useState("");

  useEffect(() => {
    if (!isOpen) {
      setDraft("");
      setSearch("");
      return undefined;
    }
    const timer = setTimeout(() => setSearch(draft.trim()), SOCIAL_AUTHOR_SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [draft, isOpen]);

  // Every active filter but the author itself: the menu lists who else could be picked.
  const filters = useMemo(() => {
    const { author: _author, ...rest } = topicFilters.facetFilters();
    return rest;
  }, [topicFilters]);
  const { authors } = useSocialTopicAuthors({
    token: isOpen ? universe.token : null,
    filters,
    sort: metric === "reach" ? "views" : "posts",
    limit: SOCIAL_AUTHOR_MENU_LIMIT,
    search,
    refreshKey,
  });

  const selectedKey = selectedAuthor ? socialAuthorOptionKey(selectedAuthor) : null;
  const listed = authors?.authors ?? [];
  const isSearching = search.length > 0;

  const options: Array<SocialFilterMenuOption> = [
    ...(isSearching
      ? []
      : [{ value: null, label: <span>{t("socialListening.topicPage.filters.any")}</span>, testId: "topic-filter-author-option-any" }]),
    ...listed.map((author, index) => {
      const posts = author.posts;
      return {
        value: socialAuthorOptionKey(author),
        label: <SocialAuthorOptionLabel author={author} index={index} />,
        measure: posts,
        measureLabel: formatSocialCount(posts, i18n.language),
        testId: `topic-filter-author-option-${index}`,
      };
    }),
  ];

  const onSelect = (value: string | null) => {
    if (value == null) {
      topicFilters.setFilter("author", null);
      return;
    }
    const author = listed.find((item) => socialAuthorOptionKey(item) === value);
    if (author) {
      topicFilters.setFilter("author", { platform: author.platform, handle: author.handle });
    }
  };

  return (
    <SocialFilterMenu
      label={t("socialListening.topicPage.filters.author")}
      options={options}
      selected={selectedKey}
      onSelect={onSelect}
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      trigger={trigger}
      search={{
        value: draft,
        onChange: setDraft,
        placeholder: t("socialListening.topicPage.filters.authorSearch"),
        label: t("socialListening.topicPage.filters.authorSearch"),
        emptyMessage: t("socialListening.topicPage.filters.authorEmpty"),
      }}
      isLoading={authors == null}
      testId="topic-filter-author"
    />
  );
}
